import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import { sanitize } from "dompurify";

import DeleteLFPModal from "./components/DeleteLFPModal";
import LFPForm from "./components/LFPForm";
import useLFPHandlers from "./hooks/useLFPHandlers";
import { IClubhouseTeamLFPData } from "./types/lookingforplayers";

import lfpSchema from "../Tryouts/lib/lfpSchema";

import { selectSelectedTeam } from "../../../../features/clubhouse/clubhouseSlice";
import {
    selectAgeLevels,
    selectPositions,
    selectSetting,
} from "../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { ILookingForPlayers } from "../../../../types/api";

const LookingForPlayers = (): React.JSX.Element => {
    const [show, setShow] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const positions = useAppSelector(selectPositions);
    const ageLevels = useAppSelector(selectAgeLevels);
    const selectedTeam = useAppSelector(selectSelectedTeam);
    const pageContent = useAppSelector(state =>
        selectSetting(state, "majorybl_looking_for_players_text"),
    );

    const { setLookingList, looking: contextLooking } = useOutletContext()
        ? useOutletContext<{
              setLookingList: React.Dispatch<
                  React.SetStateAction<ILookingForPlayers[]>
              >;
              looking: ILookingForPlayers | undefined;
          }>()
        : { setLookingList: undefined, looking: undefined };

    const [looking, setLooking] = useState<
        Partial<ILookingForPlayers> | null | undefined
    >(contextLooking || selectedTeam?.lookingForPlayers);

    const methods = useForm<IClubhouseTeamLFPData>({
        mode: "onChange",
        defaultValues: {
            contactEmail: selectedTeam?.lookingForPlayers?.contactEmail || "",
            contactNumber: selectedTeam?.lookingForPlayers?.contactNumber || "",
            contactSite: selectedTeam?.lookingForPlayers?.contactSite || "",
            contactName: selectedTeam?.lookingForPlayers?.contactName || "",
            description: selectedTeam?.lookingForPlayers?.description || "",
            positions: selectedTeam?.lookingForPlayers?.position
                ? selectedTeam.lookingForPlayers.position.map(p => {
                      return {
                          value: positions.find(i => i.position === p)?.id,
                          label: p,
                      };
                  })
                : [],
            ageLevels: selectedTeam?.lookingForPlayers?.age
                ? selectedTeam.lookingForPlayers.age.map(a => {
                      return {
                          value: ageLevels.find(i => i.ageLevel === a)?.id,
                          label: a,
                      };
                  })
                : [],
        },
        resolver: yupResolver(lfpSchema),
    });

    const { handleSubmit, handleDelete } = useLFPHandlers({
        setShow,
        setSending,
        setLooking,
        setLookingList,
        methods,
    });

    return (
        <section
            className="teamLookingForPlayers"
            data-testid="teamLookingForPlayers"
        >
            <div
                className="no-bottom-pad"
                dangerouslySetInnerHTML={{
                    __html: sanitize(pageContent?.setting),
                }}
            />
            <LFPForm
                sending={sending}
                methods={methods}
                handleSubmit={handleSubmit}
                setShow={setShow}
                looking={looking}
                positions={positions}
                ageLevels={ageLevels}
            />
            <DeleteLFPModal
                show={show}
                setShow={setShow}
                handleDelete={handleDelete}
            />
        </section>
    );
};

export default LookingForPlayers;
